import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SecurityIcon from "@mui/icons-material/Security";
import DevicesIcon from "@mui/icons-material/Devices";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import PeopleIcon from "@mui/icons-material/People";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import {
  updateRegisteredEmailAPI,
  getTrustedDevicesAPI,
  removeTrustedDeviceAPI,
  requestEmailChangeOTPAPI,
  verifyEmailChangeOTPAPI,
  getEmployeesForDeviceAPI,
  getDeviceAssignmentsAPI,
  assignEmployeesToDeviceAPI,
} from "../../../../API/Login/DeviceAuthAPI";
import { getDeviceId } from "../../../../Js/deviceFingerprint";
import { useSelector } from "react-redux";

const DeviceManagement = () => {
  const [newEmail, setNewEmail] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [devices, setDevices] = useState([]);
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [emailChangeDialog, setEmailChangeDialog] = useState(false);
  const [otpDialog, setOtpDialog] = useState(false);
  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  
  // Device assignment states
  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  // Get login response to fetch current email
  const loginResponse = useSelector((state) => state.loginResponseSlice.value);

  useEffect(() => {
    loadDevices();
    initCurrentDevice();
    loadCurrentEmail();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const initCurrentDevice = async () => {
    const id = await getDeviceId();
    setCurrentDeviceId(id);
  };

  const loadDevices = async () => {
    setLoading(true);
    try {
      const response = await getTrustedDevicesAPI();
      setDevices(response.devices || []);
    } catch (err) {
      console.error("Failed to load devices:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentEmail = async () => {
    try {
      // Fetch current registered email from profile
      const response = await getTrustedDevicesAPI();
      if (response.registeredEmail) {
        setCurrentEmail(response.registeredEmail);
      } else {
        // If no registered email, try to get from company profile (companyEmail)
        const companyProfileData = loginResponse?.data;
        if (companyProfileData?.profile?.companyEmail) {
          setCurrentEmail(companyProfileData.profile.companyEmail);
        }
      }
    } catch (err) {
      console.error("Failed to load email:", err);
    }
  };

  const handleEditEmail = () => {
    setNewEmail(currentEmail || "");
    setIsEditing(true);
    setEmailChangeDialog(true);
  };

  const handleRequestEmailChange = async () => {
    if (!newEmail || !validateEmail(newEmail)) {
      alert("Please enter a valid email address");
      return;
    }

    if (newEmail === currentEmail) {
      alert("New email is same as current email");
      return;
    }

    setLoading(true);
    try {
      // Request OTP to current email
      await requestEmailChangeOTPAPI({ newEmail });
      setEmailChangeDialog(false);
      setOtpDialog(true);
      setCountdown(60);
    } catch (err) {
      console.error("Failed to request OTP:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndUpdateEmail = async () => {
    if (!otp || otp.length !== 6) {
      alert("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      await verifyEmailChangeOTPAPI({ newEmail, otp });
      setCurrentEmail(newEmail);
      setOtpDialog(false);
      setOtp("");
      setNewEmail("");
      setIsEditing(false);
      await loadCurrentEmail();
    } catch (err) {
      console.error("Failed to verify OTP:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDevice = async () => {
    if (!deviceToDelete) return;

    setLoading(true);
    try {
      await removeTrustedDeviceAPI({ deviceId: deviceToDelete.deviceId });
      setDeleteDialog(false);
      setDeviceToDelete(null);
      await loadDevices();
    } catch (err) {
      console.error("Failed to remove device:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignment = async (device) => {
    setSelectedDevice(device);
    setSelectedEmployees(device.assignedEmployees || []);
    
    // Load all employees
    setLoading(true);
    try {
      const response = await getEmployeesForDeviceAPI();
      setAllEmployees(response.employees || []);
      setAssignmentDialog(true);
    } catch (err) {
      console.error("Failed to load employees:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignment = async () => {
    if (!selectedDevice) return;

    setLoading(true);
    try {
      await assignEmployeesToDeviceAPI({
        deviceId: selectedDevice.deviceId,
        employeeIds: selectedEmployees,
      });
      
      // Reload devices to show updated assignments
      await loadDevices();
      setAssignmentDialog(false);
      setSelectedDevice(null);
      setSelectedEmployees([]);
    } catch (err) {
      console.error("Failed to save assignment:", err);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Device Security Management
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Manage your registered email and trusted devices for enhanced login security.
        When logging in from a new device, you'll receive an OTP to your registered email.
      </Alert>

      {/* Registered Email Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <EmailIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Registered Email
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            This email will be used to send OTP for device verification
          </Typography>
          
          {currentEmail ? (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <TextField
                  fullWidth
                  label="Current Email"
                  variant="outlined"
                  value={currentEmail}
                  disabled
                  type="email"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleEditEmail}
                  startIcon={<EditIcon />}
                  sx={{ minWidth: "120px", height: "56px" }}
                >
                  Change
                </Button>
              </Box>
              <Alert severity="info" sx={{ mt: 1 }}>
                To change your email, you'll need to verify with OTP sent to your current email
              </Alert>
            </Box>
          ) : (
            <Alert severity="warning">
              No registered email found. Please contact administrator to set up your email.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Trusted Devices Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <DevicesIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Trusted Devices ({devices.length})
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            These devices can login without OTP verification
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : devices.length === 0 ? (
            <Alert severity="warning">
              No trusted devices found. After your first login with OTP verification,
              this device will be added to the trusted list.
            </Alert>
          ) : (
            <List>
              {devices.map((device, index) => (
                <React.Fragment key={device.deviceId}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {device.deviceName || "Unknown Device"}
                          </Typography>
                          {device.deviceId === currentDeviceId && (
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: "success.main",
                                color: "white",
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              Current Device
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block">
                            Added: {formatDate(device.addedOn)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Last Login: {formatDate(device.lastLogin)}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                          >
                            ID: {device.deviceId.substring(0, 16)}...
                          </Typography>
                          {device.assignedEmployees && device.assignedEmployees.length > 0 && (
                            <Typography variant="caption" display="block" color="primary">
                              👥 {device.assignedEmployees.length} employee(s) assigned
                            </Typography>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PeopleIcon />}
                        onClick={() => handleOpenAssignment(device)}
                        sx={{ mr: 1 }}
                      >
                        Assign ({device.assignedEmployees?.length || 0})
                      </Button>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => {
                          setDeviceToDelete(device);
                          setDeleteDialog(true);
                        }}
                        disabled={device.deviceId === currentDeviceId}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Remove Trusted Device?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this device? You'll need to verify with OTP
            the next time you login from this device.
          </Typography>
          {deviceToDelete && (
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2">
                <strong>Device:</strong> {deviceToDelete.deviceName}
              </Typography>
              <Typography variant="body2">
                <strong>Added:</strong> {formatDate(deviceToDelete.addedOn)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveDevice}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Change Dialog */}
      <Dialog open={emailChangeDialog} onClose={() => setEmailChangeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Registered Email</DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              An OTP will be sent to your current email ({currentEmail}) to verify this change.
            </Alert>
            <TextField
              fullWidth
              label="Current Email"
              variant="outlined"
              value={currentEmail}
              disabled
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="New Email Address"
              variant="outlined"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              type="email"
              placeholder="new.email@example.com"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailChangeDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRequestEmailChange}
            variant="contained"
            color="primary"
            disabled={loading || !newEmail}
          >
            {loading ? <CircularProgress size={24} /> : "Send OTP"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP Verification Dialog */}
      <Dialog open={otpDialog} onClose={() => setOtpDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify Email Change</DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Enter the OTP sent to your current email: <strong>{currentEmail}</strong>
            </Alert>
            <Typography variant="body2" color="textSecondary" mb={2}>
              New email will be: <strong>{newEmail}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Enter OTP"
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: { textAlign: "center", fontSize: "24px", letterSpacing: "8px" },
              }}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === "Enter" && otp.length === 6) {
                  handleVerifyAndUpdateEmail();
                }
              }}
            />
            <Box mt={2} textAlign="center">
              <Button
                variant="text"
                size="small"
                onClick={handleRequestEmailChange}
                disabled={loading || countdown > 0}
              >
                {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
              </Button>
            </Box>
            <Typography variant="caption" color="textSecondary" display="block" mt={1} textAlign="center">
              OTP is valid for 10 minutes
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtpDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleVerifyAndUpdateEmail}
            variant="contained"
            color="primary"
            disabled={loading || otp.length !== 6}
          >
            {loading ? <CircularProgress size={24} /> : "Verify & Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Assignment Dialog */}
      <Dialog open={assignmentDialog} onClose={() => setAssignmentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PeopleIcon />
            Assign Employees to Device
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Device:</strong> {selectedDevice?.deviceName}
            </Alert>
            
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select which employees can use this device. If no employees are selected, all employees can use this device.
            </Typography>

            <Box sx={{ 
              border: '1px solid #ddd', 
              borderRadius: '4px', 
              maxHeight: '400px', 
              overflow: 'auto',
              bgcolor: '#fafafa'
            }}>
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                p: 1.5, 
                bgcolor: '#f0f0f0', 
                borderBottom: '2px solid #ddd',
                fontWeight: 'bold',
                position: 'sticky',
                top: 0,
                zIndex: 1
              }}>
                <Box sx={{ width: '60px', textAlign: 'center' }}>Select</Box>
                <Box sx={{ flex: '0 0 100px' }}>Emp Code</Box>
                <Box sx={{ flex: '1' }}>Employee Name</Box>
                <Box sx={{ flex: '0 0 150px' }}>Username</Box>
                <Box sx={{ flex: '0 0 150px' }}>Designation</Box>
              </Box>
              
              {/* Employee List */}
              {allEmployees.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
                  No employees found
                </Box>
              ) : (
                allEmployees.map((emp) => (
                  <Box
                    key={emp.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      borderBottom: '1px solid #eee',
                      bgcolor: selectedEmployees.indexOf(emp.id) > -1 ? '#e3f2fd' : 'white',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: selectedEmployees.indexOf(emp.id) > -1 ? '#bbdefb' : '#f5f5f5',
                      },
                    }}
                    onClick={() => {
                      const currentIndex = selectedEmployees.indexOf(emp.id);
                      const newSelected = [...selectedEmployees];
                      if (currentIndex === -1) {
                        newSelected.push(emp.id);
                      } else {
                        newSelected.splice(currentIndex, 1);
                      }
                      setSelectedEmployees(newSelected);
                    }}
                  >
                    <Box sx={{ width: '60px', textAlign: 'center' }}>
                      <Checkbox 
                        checked={selectedEmployees.indexOf(emp.id) > -1}
                        sx={{ p: 0 }}
                      />
                    </Box>
                    <Box sx={{ flex: '0 0 100px', fontWeight: 500 }}>
                      {emp.empCode || 'N/A'}
                    </Box>
                    <Box sx={{ flex: '1', fontWeight: 600 }}>
                      {emp.empName || 'N/A'}
                    </Box>
                    <Box sx={{ flex: '0 0 150px', color: '#666', fontSize: '0.9em' }}>
                      {emp.username || 'N/A'}
                    </Box>
                    <Box sx={{ flex: '0 0 150px', color: '#888', fontSize: '0.85em' }}>
                      {emp.designation || 'N/A'}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
            
            {/* Selected Count */}
            {selectedEmployees.length > 0 && (
              <Box sx={{ mt: 1, p: 1, bgcolor: '#e3f2fd', borderRadius: '4px' }}>
                <Typography variant="body2" color="primary">
                  <strong>{selectedEmployees.length}</strong> employee(s) selected
                </Typography>
              </Box>
            )}

            {selectedEmployees.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Only the selected {selectedEmployees.length} employee(s) will be able to login from this device.
              </Alert>
            )}
            
            {selectedEmployees.length === 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                All employees can login from this device (no restrictions).
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAssignment}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PeopleIcon />}
          >
            {loading ? "Saving..." : "Save Assignment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceManagement;
